import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import {
  canRetryJob,
  jobProgressPercent,
  statusTier,
  summarizeJob,
} from './utils/provisioning-jobs.utils';

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const FacilityBulkJobsPanel = ({
  jobs,
  loading,
  error,
  submitting,
  onRefresh,
  onRetry,
  onRefreshFacilities,
}) => {
  const handleRefresh = async () => {
    await onRefresh?.({ background: true });
    onRefreshFacilities?.({ background: true });
  };

  const renderBody = () => {
    if (loading && jobs.length === 0) {
      return <LinearProgress data-testid="facility-lms-bulk-jobs-loading" />;
    }
    if (jobs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No bulk facility provisioning jobs yet. Select facilities and use &quot;Provision selected to Canvas&quot; above.
        </Typography>
      );
    }
    return (
      <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job #</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Initiated by</TableCell>
              <TableCell>Started</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.jobId} hover data-testid={`facility-bulk-job-row-${job.jobId}`}>
                <TableCell>{job.jobId}</TableCell>
                <TableCell>
                  <Chip size="small" color={statusTier(job.status)} label={job.status} variant="outlined" />
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box flex={1}>
                      <LinearProgress variant="determinate" value={jobProgressPercent(job)} />
                    </Box>
                    <Typography variant="caption">{jobProgressPercent(job)}%</Typography>
                  </Box>
                </TableCell>
                <TableCell>{summarizeJob(job)}</TableCell>
                <TableCell>{job.initiatedBy || '—'}</TableCell>
                <TableCell>{formatTimestamp(job.startedAt || job.createdAt)}</TableCell>
                <TableCell align="right">
                  {canRetryJob(job) && (
                    <Tooltip title="Re-run location bulk provision">
                      <span>
                        <Button
                          size="small"
                          startIcon={<ReplayRoundedIcon />}
                          disabled={submitting}
                          onClick={() => onRetry?.(job.jobId)}
                          data-testid={`facility-bulk-job-retry-${job.jobId}`}
                        >
                          Retry
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 2, borderColor: 'divider', borderRadius: 2 }}
      data-testid="facility-lms-bulk-jobs-panel"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Bulk facility provisioning jobs
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Async location bulk runs (accounts.csv → Canvas SIS import). Use Refresh to update job status.
          </Typography>
        </Box>
        <Tooltip title="Refresh job status">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={loading || submitting}
              onClick={handleRefresh}
              data-testid="facility-lms-bulk-jobs-refresh"
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Failed to load bulk provisioning jobs.
        </Alert>
      )}

      {renderBody()}
    </Paper>
  );
};

FacilityBulkJobsPanel.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitting: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onRefreshFacilities: PropTypes.func,
};

FacilityBulkJobsPanel.defaultProps = {
  loading: false,
  error: null,
  submitting: false,
  onRefreshFacilities: null,
};

export default FacilityBulkJobsPanel;
